import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { Search, ArrowRight, Sparkles, BookOpen, ChevronRight, Loader2 } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import ArticleCard from '../../components/academy/ArticleCard';
import Breadcrumbs from '../../components/academy/Breadcrumbs';

const API = process.env.REACT_APP_BACKEND_URL;

const SearchPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const q = searchParams.get('q') || '';
  const mode = searchParams.get('mode') || 'search';
  const [query, setQuery] = useState(q);
  const [activeMode, setActiveMode] = useState(mode);

  // Search state
  const [results, setResults] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);

  // AI Ask state
  const [aiAnswer, setAiAnswer] = useState(null);
  const [aiSources, setAiSources] = useState([]);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiSearched, setAiSearched] = useState(false);

  useEffect(() => {
    if (q && q.trim().length >= 2) {
      if (mode === 'ask') {
        doAsk(q);
      } else {
        doSearch(q, 1);
      }
    }
  }, [q, mode]);

  const doSearch = async (searchQ, pg) => {
    setLoading(true);
    setSearched(true);
    try {
      const res = await fetch(`${API}/api/academy/search?q=${encodeURIComponent(searchQ)}&page=${pg}&limit=20`);
      const data = await res.json();
      setResults(data.articles || []);
      setTotal(data.total || 0);
      setPages(data.pages || 1);
      setPage(pg);
    } catch (e) {
      console.error('Search error:', e);
    } finally {
      setLoading(false);
    }
  };

  const doAsk = async (question) => {
    setAiLoading(true);
    setAiSearched(true);
    setAiAnswer(null);
    setAiSources([]);
    try {
      const res = await fetch(`${API}/api/academy/ask?q=${encodeURIComponent(question)}`);
      const data = await res.json();
      setAiAnswer(data.answer || '');
      setAiSources(data.sources || []);
    } catch (e) {
      console.error('Ask error:', e);
      setAiAnswer('Something went wrong. Please try again.');
    } finally {
      setAiLoading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (query.trim().length >= 2) {
      setSearchParams({ q: query.trim(), mode: activeMode });
      if (activeMode === 'ask') {
        doAsk(query.trim());
      } else {
        doSearch(query.trim(), 1);
      }
    }
  };

  const switchMode = (newMode) => {
    setActiveMode(newMode);
    if (q && q.trim().length >= 2) {
      setSearchParams({ q: q.trim(), mode: newMode });
    }
  };

  return (
    <div className="bg-[#FAFAFA] min-h-screen">
      {/* Search Header */}
      <section className="bg-white border-b border-slate-200">
        <div className="max-w-4xl mx-auto px-6 md:px-8 pt-8 pb-8">
          <Breadcrumbs searchQuery={q} />

          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 mt-6 mb-6" style={{ fontFamily: 'var(--font-heading)' }}>
            Academy Search
          </h1>

          {/* Mode Toggle */}
          <div className="flex gap-1 bg-slate-100 p-1 rounded-full mb-5 max-w-xs" data-testid="search-mode-toggle">
            <button
              onClick={() => switchMode('search')}
              data-testid="mode-search"
              className={`flex items-center gap-1.5 flex-1 text-xs font-semibold py-2 px-4 rounded-full transition-all ${
                activeMode === 'search' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'
              }`}
            >
              <Search className="h-3.5 w-3.5" /> Search
            </button>
            <button
              onClick={() => switchMode('ask')}
              data-testid="mode-ask"
              className={`flex items-center gap-1.5 flex-1 text-xs font-semibold py-2 px-4 rounded-full transition-all ${
                activeMode === 'ask' ? 'bg-[#115E59] text-white shadow-sm' : 'text-slate-500 hover:text-slate-700'
              }`}
            >
              <Sparkles className="h-3.5 w-3.5" /> Ask Quidity
            </button>
          </div>

          <form onSubmit={handleSubmit} className="relative" data-testid="search-page-form">
            {activeMode === 'ask' ? (
              <Sparkles className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-[#115E59]" />
            ) : (
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
            )}
            <input
              data-testid="search-page-input"
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={activeMode === 'ask' ? 'Ask Quidity about commercial finance...' : 'Search articles, topics, guides...'}
              className={`w-full border text-slate-900 rounded-full pl-12 pr-28 py-3.5 placeholder:text-slate-400 focus:ring-2 transition-all text-sm ${
                activeMode === 'ask'
                  ? 'bg-[#115E59]/[0.04] border-[#115E59]/20 focus:ring-[#115E59]/30 focus:border-[#115E59]'
                  : 'bg-[#FAFAFA] border-slate-200 focus:ring-[#115E59]/30 focus:border-[#115E59]'
              }`}
            />
            <button
              type="submit"
              data-testid="search-page-button"
              className="absolute right-2 top-1/2 -translate-y-1/2 bg-[#115E59] text-white text-sm font-semibold px-5 py-2 rounded-full hover:bg-[#0d4f4a] transition-colors"
            >
            {activeMode === 'ask' ? 'Ask' : 'Search'}
            </button>
          </form>

          {activeMode === 'ask' && (
            <p className="text-xs text-slate-400 mt-3 pl-1">
              Powered by Quidity Academy's knowledge base.
            </p>
          )}
        </div>
      </section>

      {/* Results */}
      <div className="max-w-7xl mx-auto px-6 md:px-8 py-10">

        {/* === ASK AI MODE === */}
        {activeMode === 'ask' && (
          <>
            {aiLoading ? (
              <div className="flex flex-col items-center justify-center py-16 gap-3">
                <Loader2 className="h-8 w-8 text-[#115E59] animate-spin" />
                <p className="text-sm text-slate-500">Researching your question across Quidity Academy articles...</p>
              </div>
            ) : aiSearched ? (
              aiAnswer ? (
                <div className="max-w-4xl mx-auto">
                  {/* AI Answer Card */}
                  <div className="bg-white border border-[#115E59]/20 rounded-2xl overflow-hidden mb-10" data-testid="ai-answer-card">
                    <div className="bg-[#115E59]/[0.04] border-b border-[#115E59]/10 px-8 py-4 flex items-center gap-2">
                      <Sparkles className="h-4 w-4 text-[#115E59]" />
                      <span className="text-xs font-bold text-[#115E59] uppercase tracking-wider">Quidity's Answer</span>
                      <span className="text-[10px] text-slate-400 ml-auto">Sourced from Quidity Academy</span>
                    </div>
                    <div className="px-8 py-6">
                      <div className="prose prose-slate prose-sm max-w-none text-slate-700 leading-relaxed ai-answer-content">
                        <ReactMarkdown
                          components={{
                            p: ({children}) => <p className="text-sm text-slate-700 leading-relaxed mb-4">{children}</p>,
                            strong: ({children}) => <strong className="font-bold text-slate-900">{children}</strong>,
                            em: ({children}) => <em className="text-slate-600 italic">{children}</em>,
                            ul: ({children}) => <ul className="list-disc pl-5 mb-4 space-y-1">{children}</ul>,
                            ol: ({children}) => <ol className="list-decimal pl-5 mb-4 space-y-1">{children}</ol>,
                            li: ({children}) => <li className="text-sm text-slate-700">{children}</li>,
                          }}
                        >
                          {aiAnswer}
                        </ReactMarkdown>
                      </div>
                    </div>
                  </div>

                  {/* Source Articles */}
                  {aiSources.length > 0 && (
                    <div>
                      <div className="flex items-center gap-2 mb-5">
                        <BookOpen className="h-4 w-4 text-[#115E59]" />
                        <span className="text-xs font-bold text-slate-900 uppercase tracking-wider">Recommended Reading</span>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {aiSources.map((s) => {
                          const categoryName = s.category ? s.category.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) : '';
                          return (
                            <Link
                              key={s.slug}
                              to={`/academy/${s.slug}`}
                              data-testid={`ai-source-${s.slug}`}
                              className="group bg-white border border-slate-200 rounded-xl p-5 hover:border-[#115E59]/40 hover:shadow-sm transition-all"
                            >
                              <div className="flex items-center gap-2 mb-2">
                                <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">{categoryName}</span>
                                {s.is_cornerstone && (
                                  <span className="text-[10px] font-bold text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded">Guide</span>
                                )}
                              </div>
                              <h3 className="text-sm font-bold text-slate-900 mb-2 leading-snug group-hover:text-[#115E59] transition-colors line-clamp-2" style={{ fontFamily: 'var(--font-heading)' }}>
                                {s.title}
                              </h3>
                              <span className="text-xs font-semibold text-[#115E59] flex items-center gap-1">
                                Read article <ArrowRight className="h-3 w-3 group-hover:translate-x-0.5 transition-transform" />
                              </span>
                            </Link>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center py-16">
                  <p className="text-slate-500">No answer could be generated. Try a different question.</p>
                </div>
              )
            ) : (
              <div className="text-center py-16 text-slate-400">
                <Sparkles className="h-12 w-12 mx-auto mb-4 opacity-20" />
                <p className="text-sm">Ask Quidity about commercial finance, SBA loans, cash flow, or any topic covered in the Academy.</p>
              </div>
            )}
          </>
        )}

        {/* === SEARCH MODE === */}
        {activeMode === 'search' && (
          <>
            {loading ? (
              <div className="flex justify-center py-16">
                <div className="w-8 h-8 border-2 border-[#115E59] border-t-transparent rounded-full animate-spin" />
              </div>
            ) : searched ? (
              <>
                <p className="text-sm text-slate-500 mb-8">
                  {total > 0 ? `Found ${total} result${total !== 1 ? 's' : ''} for "${q}"` : `No results found for "${q}"`}
                </p>

                {results.length > 0 ? (
                  <>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                      {results.map(article => (
                        <ArticleCard key={article.slug} article={article} variant="standard" />
                      ))}
                    </div>

                    {pages > 1 && (
                      <div className="flex justify-center gap-2 mt-12">
                        {Array.from({ length: pages }, (_, i) => i + 1).map(p => (
                          <button
                            key={p}
                            onClick={() => { doSearch(q, p); window.scrollTo(0, 0); }}
                            className={`w-10 h-10 rounded-full text-sm font-semibold transition-colors ${
                              p === page ? 'bg-[#115E59] text-white' : 'bg-white border border-slate-200 text-slate-600 hover:bg-slate-50'
                            }`}
                          >
                            {p}
                          </button>
                        ))}
                      </div>
                    )}
                  </>
                ) : (
                  <div className="text-center py-16">
                    <p className="text-slate-500 mb-4">Try different keywords or browse our categories.</p>
                    <Link to="/academy" className="text-[#115E59] font-semibold hover:underline">Browse Academy</Link>
                  </div>
                )}
              </>
            ) : (
              <div className="text-center py-16 text-slate-400">
                <Search className="h-12 w-12 mx-auto mb-4 opacity-30" />
                <p>Enter a search term to find articles</p>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default SearchPage;
